import { ActionTree } from 'vuex';
import { Step, TourState } from './state';
import RootState from '@/store/root_state';
import router from '@/router';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';

interface TourCallbacks {
  [index: string]: Function;
}

const findElement = (identifier: string, index: number = 0): Element => {
  return document.getElementsByClassName(identifier)[index];
};

const removeHighlight = (elementIdentifier: string) => {
  document.querySelector(elementIdentifier)?.classList.remove('highlight');
};

const conditionalCallbacks: TourCallbacks = {
  hasScheduledActivities() {
    const calendarExists = document.getElementsByClassName('card-calendar');
    return calendarExists.length > 0;
  },
};

function removeStep(steps: Step[], step: Step) {
  return steps.filter((e) => {
    return e !== step;
  });
}

const checkConditionalSteps = (steps: Step[]): Step[] => {
  let filteredSteps: Step[] = steps;
  steps.forEach((step, index) => {
    if (step.isConditional) {
      const conditionResult = conditionalCallbacks[`${step.conditionCallback}`]();
      if (conditionResult) {
        filteredSteps = removeStep(steps, steps[index + 1]);
      } else {
        filteredSteps = removeStep(steps, step);
      }
    }
  });
  return filteredSteps;
};

const tourCallbacks: TourCallbacks = {
  async goToClassrooms() {
    await router.push({
      name: 'classrooms',
    });
  },
  async goToFirstClassroom() {
    const element = document
      .getElementById('classroomsItem')
      ?.getElementsByClassName('card-discipline__link')[0];
    let classPath = element?.getAttribute('href');
    if (process.env.NODE_ENV !== 'production' && classPath?.startsWith('/PR')) {
      classPath = classPath.substring(7, classPath.length);
    }
    if (classPath) {
      await router.push({
        path: classPath,
      });
    }
  },
  async addIdToMenu() {
    let element;
    if (window.innerWidth < 640) {
      element = findElement('navigation__tab');
      element.children[1].id = 'disciplinesMenu';
    } else {
      element = findElement('menu__item', 1)?.querySelector('.menu-button');
      if (element) {
        element.id = 'disciplinesMenu';
      }
    }
  },

  async clickInLiveClassroom() {
    const element = document.getElementById('classroomItem');
    element?.dispatchEvent(new Event('click'));
  },
};

const actions: ActionTree<TourState, RootState> = {
  startTour(context, steps) {
    const filteredSteps = checkConditionalSteps(steps);
    context.commit('START_TOUR', filteredSteps);
  },
  async nextStep(context, payload): Promise<void> {
    const { currentStep, numberOfSteps, steps } = context.state;
    await Analytics.trackEventInTrial(EVENT_LABELS.buttonClicked, {
      button: `step-${currentStep}-next-step-tour`,
    });
    const { callback } = payload;
    if (currentStep === numberOfSteps - 1) {
      return;
    }

    if (callback) {
      await tourCallbacks[callback](context);
    }
    const { elementIdentifier } = steps[currentStep];
    if (elementIdentifier) {
      removeHighlight(elementIdentifier);
    }
    context.commit('NEXT_STEP');
  },
  async previousStep(context): Promise<void> {
    await Analytics.trackEventInTrial(EVENT_LABELS.buttonClicked, {
      button: 'previous-step-tour',
    });
    const { currentStep, steps } = context.state;
    if (context.getters.lastStepIsInAnotherPage) {
      router.back();
    }

    const { elementIdentifier } = steps[currentStep];
    if (elementIdentifier) {
      removeHighlight(elementIdentifier);
    }
    context.commit('PREVIOUS_STEP');
  },
  async finishTour(context) {
    await Analytics.trackEventInTrial(EVENT_LABELS.buttonClicked, {
      button: 'finish-tour',
    });
    const { currentStep, steps } = context.state;
    const { elementIdentifier } = steps[currentStep];
    if (elementIdentifier) {
      removeHighlight(elementIdentifier);
    }
    context.commit('FINISH_TOUR');
  },
};

export default actions;
